// Converts a local calendar date (year/month/day only) to a UTC-midnight
// ISO string for that day — the wire format used for date-only values like
// expiryDate. Shared by DatePickerField (user-picked dates) and anywhere
// else that needs to compute one of these values directly.
export const toUtcIsoDate = (date) => {
  if (!date) return null;
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  ).toISOString();
};

export const formatDateTime = (value, options = {}, language) => {
  const { fallback = "—", ...formatOptions } = options;
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;

  return new Intl.DateTimeFormat(language, {
    dateStyle: "medium",
    timeStyle: "short",
    ...formatOptions,
  }).format(date);
};

// Formats a date range between two values (e.g., "Oct 1, 2026 – Oct 6, 2026").
// Fully customizable via options: dateStyle, timeStyle, separator, fallback.
formatDateTime.range = (from, to, options = {}, language) => {
  const {
    dateStyle = "medium",
    timeStyle = undefined,
    separator = " – ",
    fallback = "—",
    ...formatOptions
  } = options;

  if (!from && !to) return fallback;
  if (!from)
    return formatDateTime(
      to,
      { dateStyle, timeStyle, ...formatOptions },
      language,
    );
  if (!to)
    return formatDateTime(
      from,
      { dateStyle, timeStyle, ...formatOptions },
      language,
    );

  return `${formatDateTime(from, { dateStyle, timeStyle, ...formatOptions }, language)}${separator}${formatDateTime(to, { dateStyle, timeStyle, ...formatOptions }, language)}`;
};

// Formats a date filter object ({ preset, customFrom, customTo, label }) with dynamic
// translation prefix and customizable date formatting.
// Property keys for date bounds (fromKey, toKey) are also dynamically.
formatDateTime.filterLabel = (
  dateFilter,
  {
    t,
    language,
    prefix,
    fallback,
    formatOptions,
    separator,
    fromKey = "customFrom",
    toKey = "customTo",
  } = {},
) => {
  if (!dateFilter) return "";

  const from = dateFilter[fromKey];
  const to = dateFilter[toKey];

  if (dateFilter.preset === "custom" && from && to) {
    return formatDateTime.range(
      from,
      to,
      { ...formatOptions, ...(separator && { separator }) },
      language,
    );
  }

  const key = dateFilter.preset || "custom";
  const defaultLabel = dateFilter.label || fallback || key;

  if (typeof t === "function" && prefix) {
    return (
      t(`${prefix}.${key}`, { defaultValue: "" }) ||
      t(`${prefix}.${key.toLowerCase()}`, { defaultValue: "" }) ||
      defaultLabel
    );
  }

  return defaultLabel;
};

// Fixed "en" locale + explicit format (distinct from formatDateTime's
// locale-default, dateStyle/timeStyle-based formatting) — shared by
// ManageUserForm and UserViewDialog, which both rendered this exact format.
export const formatDate = (value, fallback = "—", language) => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat(language || "en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};
